import { useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  ScrollArea,
  TextField,
  Select,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";

import styles from "../../styles.module.scss";

import { TopicWithBadges } from "./TopicsList.types";
import { TopicListItem } from "./TopicListItem";
import { EmptyState } from "./EmptyState";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { TopicType } from "./TopicsList.shared.types";

interface TopicsViewProps {
  topics: TopicWithBadges[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedType: TopicType;
  setSelectedType: (type: TopicType) => void;
  selectedTopic: Topic | null;
  onTopicSelect: (topic: string | null) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isDisabled: boolean;
}

export const TopicsView = ({
  topics,
  searchQuery,
  setSearchQuery,
  selectedType,
  setSelectedType,
  selectedTopic,
  onTopicSelect,
  isCollapsed,
  setIsCollapsed,
  isDisabled,
}: TopicsViewProps) => {
  const filteredTopics = useMemo(() => {
    return topics.filter((topicWithBadges: TopicWithBadges) => {
      const matchesSearch = topicWithBadges.topicName
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      let matchesType = true;
      if (selectedType === "live") {
        matchesType = topicWithBadges.isLive;
      } else if (selectedType === "updates") {
        matchesType = topicWithBadges.hasUpdates;
      } else if (selectedType === "new") {
        matchesType = topicWithBadges.isNew;
      } else if (selectedType === "untrained") {
        // A topic is untrained if it's not live and has no updates or new status
        matchesType =
          !topicWithBadges.isLive &&
          !topicWithBadges.hasUpdates &&
          !topicWithBadges.isNew;
      }

      return matchesSearch && matchesType;
    });
  }, [topics, searchQuery, selectedType]);

  // Update Select options to match badge terminology
  const filterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "live", label: "Live" },
    { value: "new", label: "New" },
    { value: "updates", label: "Has Updates" },
    { value: "untrained", label: "Untrained" },
  ];

  if (!topics.length) {
    return (
      <Box className={styles.topicsListContainer}>
        <Text size="2" color="gray">
          No topics available
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" className="h-full">
      <Flex direction="column" gap="3" className="p-2">
        <div className={isCollapsed ? "hidden" : ""}>
          <Flex gap="2">
            <TextField.Root
              size="2"
              variant="soft"
              color="gray"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search topics..."
              style={{ flex: 1 }}
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>

            <Select.Root
              value={selectedType}
              onValueChange={(value) => setSelectedType(value as TopicType)}
            >
              <Select.Trigger style={{ width: "140px" }} />
              <Select.Content position="popper" sideOffset={5}>
                <Select.Group>
                  {filterOptions.map((option) => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
        </div>
      </Flex>

      {!isCollapsed && (
        <ScrollArea className="flex-1">
          <Flex direction="column" gap="2">
            <Flex
              justify="between"
              align="center"
              minWidth="240px"
              className={`${styles.topicItem} ${
                selectedTopic === null ? styles.topicItemActive : ""
              } flex-grow`}
              onClick={() => (isDisabled ? undefined : onTopicSelect(null))}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
            >
              <Text
                size="2"
                weight="regular"
                className={isCollapsed ? "hidden" : ""}
              >
                All Topics
              </Text>
            </Flex>



            {filteredTopics.length > 0 ? (
              filteredTopics.map((topicWithBadges: TopicWithBadges) => (
                <TopicListItem
                  key={topicWithBadges.topicName}
                  topic={topicWithBadges.topicName}
                  badges={topicWithBadges.badges}
                  isSelected={
                    selectedTopic?.topic === topicWithBadges.topicName
                  }
                  onSelect={() => onTopicSelect(topicWithBadges.topicName)}
                  disabled={isDisabled}
                  isCollapsed={isCollapsed}
                />
              ))
            ) : (
              <EmptyState
                searchQuery={searchQuery}
                selectedType={selectedType}
                isCollapsed={isCollapsed}
              />
            )}
          </Flex>
        </ScrollArea>
      )}
    </Flex>
  );
};
